import appConfig from "@/config/appConfig";
import { userRole } from "@/constant";
import { useCreateDashboardMutation, useLazyGetByIdDashboardQuery, useUpdateDashboardMutation } from "@/redux/api/dashboardApi";
import { useSearchQuery } from "@/utils/useSearchQuery";
import { flare } from "@flareapp/js";
import { App, Button, Empty, Spin, Switch } from "antd";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { BiMessageSquareEdit, BiMinus, BiPlus } from "react-icons/bi";
import { FaPlus, FaArrowLeft } from "react-icons/fa6";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { triggerSave, setShowComponentSelect } from "@/redux/slices/liveDashboard.slice";
import { popFromHistory } from "@/redux/slices/dashboardV2.slice";
import { setActiveDashboard } from "@/redux/slices/user.slice";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";
import DashboardAddItemModal from "./DashboardAddItemModal";
import DashboardComponentModal from "./DashboardComponentModal";
import DashboardLayout from "./DashboardLayout";
import TileDashboardViewport from "@/components/TileDashboard/TileDashboardViewport";
import MetaDashboardModal from "./MetaDashboardModal";
import DashboardTabs from "./tabs/DashboardTabs";
import { ReactFlowProvider } from "@xyflow/react";
import LiveDashboardCanvas from "@/components/live-dashboard/canvas/LiveDashboardCanvas";
import { useSmoothTabZoom } from "@/hooks/useSmoothTabZoom";

const DashboardBody = ({ mode, id, isFullScreenActive = false, setTabId = () => {}, data, setData = () => {} }) => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);
  const { prevDashboardHistory } = useSelector((state) => state.dashboardV2) || {};
  const firstBusiness = userDetails?.business?.[0] || {};
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const cp = useSearchQuery("cp") || undefined;

  const [isEditable, setIsEditable] = useState(false);

  // ponytail: ensure data belongs to the active dashboard id, avoiding stale canvas bleed
  const isDataMatch = !data?._id || !id || data._id === id;
  const currentData = isDataMatch ? data : {};

  // derived state based on 'data' single source of truth
  const tabData = currentData?.tabs || [];
  // ponytail: activeTab tracks isSelected state directly so switching tabs immediately updates canvas
  const activeTabIdx = tabData.findIndex((t) => t?.isSelected);
  const _activeTabIdx = activeTabIdx !== -1 && activeTabIdx !== undefined ? activeTabIdx : 0;
  const activeTab = tabData[_activeTabIdx] || {};

  const isLive = isDataMatch && currentData?.type === "live";
  const hasTabs = Boolean(currentData?.hasTabs);
  const layouts = hasTabs ? activeTab?.layouts || currentData?.layouts || {} : currentData?.tabs?.[0]?.layouts || currentData?.layouts || {};
  const gridData = hasTabs ? activeTab?.items || currentData?.items || [] : currentData?.tabs?.[0]?.items || currentData?.items || [];
  const iframe = hasTabs ? activeTab?.iframe || currentData?.iframe : currentData?.tabs?.[0]?.iframe || currentData?.iframe;
  const tileViewport = hasTabs ? activeTab?.tileViewport : currentData?.tabs?.[0]?.tileViewport;
  const liveViewport = hasTabs ? activeTab?.liveCanvas?.viewport : (activeTab?.liveCanvas?.viewport || currentData?.liveCanvas?.viewport);
  const currentViewport = isLive ? liveViewport : tileViewport;

  const { tabZoom, targetTabZoom, handleTabZoomChange, handleResetTabZoom } = useSmoothTabZoom(1);

  const renderTabZoomControls = () => {
    if (!data?.hasTabs || !isEditable || mode === "view") return null;
    return (
      <div className="dashboard-tab-zoom-controls-theme">
        <span className="px-2 small fw-medium" style={{ color: "var(--color-black)", fontSize: "12px", whiteSpace: "nowrap" }}>
          Tabs Zoom:
        </span>
        <button
          type="button"
          className="zoom-btn"
          onClick={() => handleTabZoomChange(-0.1)}
          disabled={targetTabZoom <= 0.5}
          title="Zoom Out Tabs"
          aria-label="Zoom Out Tabs"
        >
          <BiMinus size={14} />
        </button>
        <span
          className="zoom-badge"
          onClick={handleResetTabZoom}
          title="Click to reset zoom"
        >
          {Math.round(targetTabZoom * 100)}%
        </span>
        <button
          type="button"
          className="zoom-btn"
          onClick={() => handleTabZoomChange(0.1)}
          disabled={targetTabZoom >= 1.6}
          title="Zoom In Tabs"
          aria-label="Zoom In Tabs"
        >
          <BiPlus size={14} />
        </button>
      </div>
    );
  };


  // Callback to persist tile viewport changes to data state (saved on dashboard Save)
  const onTileViewportChange = useCallback((viewport) => {
    setData((prev) => {
      const idx = prev?.tabs?.findIndex((t) => t?.isSelected);
      const currIdx = idx !== -1 && idx !== undefined ? idx : 0;
      const currentVp = prev?.tabs?.[currIdx]?.tileViewport;
      if (
        currentVp &&
        Math.abs(currentVp.x - viewport.x) < 1 &&
        Math.abs(currentVp.y - viewport.y) < 1 &&
        Math.abs(currentVp.zoom - viewport.zoom) < 0.01
      ) {
        return prev;
      }
      const newTabs = [...(prev.tabs || [])];
      if (!newTabs[currIdx]) newTabs[currIdx] = {};
      newTabs[currIdx] = { ...newTabs[currIdx], tileViewport: { x: viewport.x, y: viewport.y, zoom: viewport.zoom } };
      return { ...prev, tabs: newTabs };
    });
  }, [setData]);

  // modal
  const [updateDashboardModal, setUpdateDashboardModal] = useState(false);
  const [componentModal, setComponentModal] = useState(false);
  const [addItemModal, setAddItemModal] = useState(false);
  const [editItem, setEditItem] = useState({});

  // generic setters that update the single 'data' state correctly whether 'hasTabs' is true or false
  const setLayouts = useCallback((updater) => {
    setData((prev) => {
      const idx = prev?.tabs?.findIndex((t) => t?.isSelected);
      const currIdx = idx !== -1 && idx !== undefined ? idx : 0;
      let currentLayouts = prev?.hasTabs ? prev.tabs?.[currIdx]?.layouts || {} : prev?.tabs?.[0]?.layouts || {};
      const nextLayouts = typeof updater === "function" ? updater(currentLayouts) : updater;

      if (prev?.hasTabs) {
        const newTabs = [...(prev.tabs || [])];
        if (newTabs[currIdx]) {
          newTabs[currIdx] = { ...newTabs[currIdx], layouts: nextLayouts };
        }
        return { ...prev, tabs: newTabs };
      } else {
        const newTabs = [...(prev.tabs || [])];
        if (!newTabs[0]) newTabs[0] = {};
        newTabs[0] = { ...newTabs[0], layouts: nextLayouts };
        return { ...prev, tabs: newTabs };
      }
    });
  }, [setData]);

  const setGridData = useCallback((updater) => {
    setData((prev) => {
      const idx = prev?.tabs?.findIndex((t) => t?.isSelected);
      const currIdx = idx !== -1 && idx !== undefined ? idx : 0;
      let currentItems = prev?.hasTabs ? prev.tabs?.[currIdx]?.items || [] : prev?.tabs?.[0]?.items || [];
      const nextItems = typeof updater === "function" ? updater(currentItems) : updater;

      if (prev?.hasTabs) {
        const newTabs = [...(prev.tabs || [])];
        if (newTabs[currIdx]) {
          newTabs[currIdx] = { ...newTabs[currIdx], items: nextItems };
        }
        return { ...prev, tabs: newTabs };
      } else {
        const newTabs = [...(prev.tabs || [])];
        if (!newTabs[0]) newTabs[0] = {};
        newTabs[0] = { ...newTabs[0], items: nextItems };
        return { ...prev, tabs: newTabs };
      }
    });
  }, [setData]);

  const setIframe = (updater) => {
    setData((prev) => {
      const idx = prev?.tabs?.findIndex((t) => t?.isSelected);
      const currIdx = idx !== -1 && idx !== undefined ? idx : 0;
      let currentIframe = prev?.hasTabs ? prev.tabs?.[currIdx]?.iframe : prev?.tabs?.[0]?.iframe;
      const nextIframe = typeof updater === "function" ? updater(currentIframe) : updater;

      if (prev?.hasTabs) {
        const newTabs = [...(prev.tabs || [])];
        if (newTabs[currIdx]) {
          newTabs[currIdx] = { ...newTabs[currIdx], iframe: nextIframe, items: undefined, layouts: undefined };
        }
        return { ...prev, tabs: newTabs };
      } else {
        const newTabs = [...(prev.tabs || [])];
        if (!newTabs[0]) newTabs[0] = {};
        newTabs[0] = { ...newTabs[0], iframe: nextIframe, items: undefined, layouts: undefined };
        return { ...prev, tabs: newTabs, iframe: nextIframe };
      }
    });
  };

  // console.log({ gridData, editItem });

  // rkt query
  const [getDashboardById, { isFetching: isFetchingDashboard }] = useLazyGetByIdDashboardQuery();
const makeCopyName = (n) => {
  if (!n) return "Dashboard (Copy)";
  if (/\(Copy\s*\d*\)$/i.test(n)) {
    const match = n.match(/\(Copy\s*(\d*)\)$/i);
    const num = match && match[1] ? parseInt(match[1], 10) + 1 : 2;
    return n.replace(/\(Copy\s*\d*\)$/i, `(Copy ${num})`);
  }
  return `${n} (Copy)`;
};

const cleanTabForCopy = (tab, idx = 0) => {
  const safeTab = tab && typeof tab === "object" ? tab : {};
  const { _id, ...restTab } = safeTab;
  const cleanedTab = {
    ...restTab,
    name: restTab.name || `Tab ${idx + 1}`,
    isSelected: restTab.isSelected ?? (idx === 0),
    tabId: uuid(),
  };
  if (cleanedTab.liveCanvas) {
    const { nodes = [], edges = [], ...restCanvas } = cleanedTab.liveCanvas;
    cleanedTab.liveCanvas = {
      ...restCanvas,
      nodes: (nodes || []).map(({ _id, ...n }) => n),
      edges: (edges || []).map(({ _id, ...e }) => e),
    };
  }
  return cleanedTab;
};

const cleanCanvasForCopy = (canvas) => {
  if (!canvas) return canvas;
  const { nodes = [], edges = [], ...restCanvas } = canvas;
  return {
    ...restCanvas,
    nodes: (nodes || []).map(({ _id, ...n }) => n),
    edges: (edges || []).map(({ _id, ...e }) => e),
  };
};

const getEntityIds = (val) => {
  if (!val) return undefined;
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    const ids = val.map((item) => (typeof item === "object" ? item?._id || item?.id : item)).filter(Boolean);
    if (ids.length === 0) return undefined;
    return ids.length === 1 ? ids[0] : ids;
  }
  if (typeof val === "object") return val._id || val.id;
  return undefined;
};

  const [createDashboard, { isLoading: isCreateLoading }] = useCreateDashboardMutation();
  const [updateDashboard, { isLoading: isUpdateLoading }] = useUpdateDashboardMutation();

  const { name, business, site } = data;

  const getDashboard = async () => {
    try {
      const { data } = await getDashboardById({ id }).unwrap();

      let dashboardData = data;
      if (cp === "true") {
        dashboardData = {
          ...dashboardData,
          name: makeCopyName(dashboardData.name),
        };
      }

      const targetTabId = new URLSearchParams(window.location.search).get("tabId");
      if (dashboardData?.hasTabs) {
        if (!dashboardData?.tabs || dashboardData.tabs.length === 0) {
          dashboardData.tabs = [
            {
              name: "Tab 1",
              tabId: uuid(),
              isSelected: true,
              ...(dashboardData?.type === "live"
                ? { liveCanvas: dashboardData.liveCanvas || {} }
                : { items: dashboardData.items || [], layouts: dashboardData.layouts || {} }),
            },
          ];
        } else {
          // If tab 0 has no liveCanvas or empty nodes, but root has liveCanvas nodes, migrate to tab 0
          if (dashboardData?.type === "live") {
            const tab0 = dashboardData.tabs[0];
            const hasTab0Nodes = tab0.liveCanvas?.nodes && tab0.liveCanvas.nodes.length > 0;
            if (!hasTab0Nodes && dashboardData.liveCanvas?.nodes?.length > 0) {
              dashboardData.tabs[0] = {
                ...tab0,
                liveCanvas: dashboardData.liveCanvas,
              };
            }
          }

          const hasMatch = targetTabId && dashboardData.tabs.some((tab) => tab.tabId === targetTabId || tab._id === targetTabId);
          const matchIdx = hasMatch ? dashboardData.tabs.findIndex((tab) => tab.tabId === targetTabId || tab._id === targetTabId) : 0;

          dashboardData = {
            ...dashboardData,
            tabs: dashboardData.tabs.map((tab, idx) => ({
              ...tab,
              isSelected: idx === matchIdx,
            })),
          };
        }
      } else {
        if (dashboardData?.type === "live") {
          const rootNodes = dashboardData.liveCanvas?.nodes || [];
          const tab0Nodes = dashboardData.tabs?.[0]?.liveCanvas?.nodes || [];
          if (rootNodes.length === 0 && tab0Nodes.length > 0) {
            dashboardData.liveCanvas = dashboardData.tabs[0].liveCanvas;
          } else if (rootNodes.length > 0 && tab0Nodes.length === 0) {
            if (dashboardData.tabs && dashboardData.tabs[0]) {
              dashboardData.tabs[0] = { ...dashboardData.tabs[0], liveCanvas: dashboardData.liveCanvas };
            }
          }
        }
      }
      const activeTab = dashboardData?.tabs?.find((t) => t?.isSelected) || dashboardData?.tabs?.[0];
      setData(dashboardData);
      setTabId(activeTab?._id || activeTab?.tabId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const handleUpdate = async () => {
    const currentTabs = data?.tabs || [];
    const isTabbed = Boolean(data?.hasTabs);

    const body = {
      name: data?.name || name,
      business: data?.business || business,
      site: data?.site || site,
      hasTabs: isTabbed,
    };

    if (data?.type === "live") {
      if (isTabbed) {
        body.tabs = (currentTabs || []).map((t, i) => ({ ...t, name: t.name || `Tab ${i + 1}` }));
      } else {
        body.tabs = (currentTabs || []).map((t, i) => ({ ...t, name: t.name || `Tab ${i + 1}` }));
        if (data?.liveCanvas) {
          body.liveCanvas = data.liveCanvas;
        }
        dispatch(triggerSave());
      }
    } else {
      if (isTabbed) {
        body.tabs = currentTabs.map((tab, i) => {
          const tabName = tab.name || `Tab ${i + 1}`;
          if (tab.isSelected) {
            return { ...tab, name: tabName, layouts, items: gridData, iframe };
          }
          return { ...tab, name: tabName };
        });
      } else {
        const sourceTab = Array.isArray(currentTabs) && currentTabs.length > 0 ? currentTabs[0] : {};
        const tabName = sourceTab.name || "Tab 1";
        if (editItem.component === "iframe" || iframe) {
          body.tabs = [{ ...sourceTab, name: tabName, iframe }];
        } else {
          body.tabs = [{ ...sourceTab, name: tabName, layouts, items: gridData, isSelected: true }];
        }
      }
    }

    try {
      const resData = await updateDashboard({ id, body }).unwrap();
      if (resData.success) {
        if (data?.type !== "live" || isTabbed) {
          notification.success({ key: "dashboard-save-notification", message: `Dashboard updated successfully.`, duration: 2, showProgress: true });
        }
        getDashboard();
      }
    } catch (error) {
      flare.report(error);
      notification.error({ message: `Error happened.`, duration: 2, showProgress: true });
    }
  };

  const handleCreate = async () => {
    const dashboardType = uuid();
    const isTabbed = Boolean(data?.hasTabs || hasTabs);
    const rawBus = data?.business || business;
    const rawSite = data?.site || site;

    const body = {
      ...editItem,
      name: data?.name || name || "New Dashboard",
      business: getEntityIds(rawBus),
      site: getEntityIds(rawSite),
      dashboardType,
      hasTabs: isTabbed,
      type: data?.type || "tile",
    };

    if (data?.type === "live") {
      const rawCanvas =
        (data?.liveCanvas?.nodes?.length ? data?.liveCanvas : null) ||
        (data?.tabs?.[0]?.liveCanvas?.nodes?.length ? data?.tabs?.[0]?.liveCanvas : null) ||
        data?.liveCanvas ||
        data?.tabs?.[0]?.liveCanvas;
      const sourceCanvas = cleanCanvasForCopy(rawCanvas);

      if (isTabbed) {
        body.tabs = (tabData || []).map((t, i) => {
          const cleaned = cleanTabForCopy(t, i);
          if (!cleaned.liveCanvas || !cleaned.liveCanvas.nodes?.length) {
            cleaned.liveCanvas = sourceCanvas;
          }
          return cleaned;
        });
      } else {
        const sourceTab = Array.isArray(tabData) && tabData.length > 0 ? tabData[0] : {};
        const cleanedTab = cleanTabForCopy(sourceTab, 0);
        cleanedTab.liveCanvas = sourceCanvas;
        body.tabs = [cleanedTab];
        body.liveCanvas = sourceCanvas;
      }
    } else {
      if (isTabbed) {
        body.tabs = (tabData || []).map((t, i) => cleanTabForCopy({ ...t, layouts: t.layouts || layouts, items: t.items || gridData }, i));
      } else {
        const sourceTab = Array.isArray(tabData) && tabData.length > 0 ? tabData[0] : {};
        body.tabs = [cleanTabForCopy({ ...sourceTab, layouts, items: gridData }, 0)];
      }
    }
    try {
      const response = await createDashboard(body).unwrap();
      const createdId = response?.result?._id || response?.data?._id || response?._id || response?.result || response?.data;
      if (response?.success || createdId) {
        notification.success({ key: "dashboard-save-notification", message: `Dashboard duplicated successfully.`, duration: 2, showProgress: true });
        return createdId || true;
      }
    } catch (error) {
      flare.report(error);
      notification.error({ message: `Error happened.`, duration: 2, showProgress: true });
      return null;
    }
  };

  const handleSubmit = async () => {
    if (cp === "true") {
      const newId = await handleCreate();
      if (newId) {
        navigate("/dashboard-list");
      }
    } else {
      await handleUpdate();
      navigate("/dashboard-list");
    }
  };

  // set filterData

  useEffect(() => {
    if (mode === "edit") {
      setIsEditable(true);
    }
  }, [mode]);

  useEffect(() => {
    if (isFullScreenActive) {
      setIsEditable(false);
    }
  }, [isFullScreenActive]);

  useEffect(() => {
    // Only clear data if dashboard id has changed, preserving cached data during page navigation
    if (data?._id && data._id !== id) {
      setData({});
    }
    if (id) {
      getDashboard();
    }
  }, [id]);

  useEffect(() => {
    document.title = `${appConfig.name} - Dashboard`;
  }, []);

  const toggleHandler = (e) => {
    // ponytail: do not set isEditable(false) before user confirms save changes
    if (e) {
      setIsEditable(true);
    } else {
      Swal.fire({
        title: "Save Changes?",
        text: "By clicking confirm, the changes will be saved. Click cancel to abandon any edits.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          if (cp === "true") {
            const createdId = await handleCreate();
            if (createdId) {
              navigate("/dashboard-list");
            }
          } else if (data?.type === "live") {
            if (data?.hasTabs && data?.tabs?.length > 0) {
              try {
                const resData = await updateDashboard({
                  id,
                  body: {
                    name: data?.name || name,
                    business: data?.business || business,
                    site: data?.site || site,
                    hasTabs: true,
                    tabs: data.tabs,
                  },
                }).unwrap();
                if (resData?.success || resData) {
                  notification.success({ key: "dashboard-save-notification", message: "Dashboard saved successfully", duration: 2, showProgress: true });
                  getDashboard();
                }
              } catch (err) {
                flare.report(err);
                notification.error({ message: "Failed to save dashboard" });
              }
            } else {
              dispatch(triggerSave());
            }
          } else {
            handleUpdate();
          }
          setIsEditable(false);
        } else {
          setIsEditable(false);
          if (cp === "true") {
            navigate("/dashboard-list");
          } else {
            getDashboard();
          }
        }
      });
    }
  };

  const handleBackNav = () => {
    if (prevDashboardHistory && prevDashboardHistory.length > 0) {
      const prev = prevDashboardHistory[prevDashboardHistory.length - 1];
      dispatch(popFromHistory());
      if (typeof prev === "object" && prev !== null) {
        if (prev.activeDashboardId) {
          dispatch(setActiveDashboard(prev.activeDashboardId));
        }
        let targetUrl = prev.url || "/";
        if (prev.activeTabId && !targetUrl.includes("tabId=")) {
          targetUrl += (targetUrl.includes("?") ? "&" : "?") + `tabId=${prev.activeTabId}`;
        }
        navigate(targetUrl);
      } else if (typeof prev === "string") {
        navigate(prev);
      }
    } else {
      navigate("/dashboard-list");
    }
  };

  const logo = firstBusiness?.logo || "/img/logo.png";
  const loading = (isFetchingDashboard || isCreateLoading || isUpdateLoading || !isDataMatch) && !isLive;
  return (
    <Spin spinning={loading} size="large" className="w-100">
      <div>
        {/* <h5>Dashboard List</h5> */}
        <div className="d-sm-flex align-items-center justify-content-between">
          {mode === "home" && isFullScreenActive && (
            <div className="p-3 ps-2">
              <img src={logo} alt="Logo" style={{ height: "50px" }} />
            </div>
          )}
          {mode !== "home" && (
            <div>
              <label className="az-content-label">Manage Dashboard</label>
              <span className="d-block py-2">
                {mode === "view" ? "View" : "Edit"} Dashboard: {data?.name}
              </span>
            </div>
          )}

          {/* edit options & navigation */}
          <div className="d-flex align-items-center gap-2 ms-2 ms-sm-auto me-2 me-md-4 flex-wrap justify-content-end">
            {![userRole.public, userRole.user].includes(userDetails?.role) && mode !== "view" && isEditable && (
              <>
                <Button
                  className="fw-medium bg_light_gray"
                  type="primary"
                  icon={<BiMessageSquareEdit />}
                  onClick={() => setUpdateDashboardModal((pre) => !pre)}
                >
                  Edit Dashboard
                </Button>
                <Button
                  type="primary"
                  className="fw-medium bg_light_gray"
                  onClick={() => {
                    if (data?.type === "live") {
                      dispatch(setShowComponentSelect(true));
                    } else {
                      setComponentModal((pre) => !pre);
                    }
                  }}
                  icon={<FaPlus />}
                  disabled={data.hasTabs && !tabData?.find((tab) => tab.isSelected)}
                >
                  Add Items
                </Button>
              </>
            )}
            {mode !== "home" && renderTabZoomControls()}
            {mode === "home" && !isFullScreenActive && (
              <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                {prevDashboardHistory && prevDashboardHistory.length > 0 && (
                  <Button type="default" icon={<FaArrowLeft />} onClick={handleBackNav}>
                    Back
                  </Button>
                )}
                {renderTabZoomControls()}
                {![userRole.public, userRole.user].includes(userDetails?.role) && mode !== "view" && (
                  <div className="d-flex align-items-center">
                    <Switch checked={isEditable} onChange={toggleHandler} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {mode !== "home" && <hr className="mt-0 mb-4" />}

        {/* tabs or spacing when no tabs */}
        {data.hasTabs ? (
          <div className="mb-3">
            <DashboardTabs
              {...{
                mode: [userRole.public, userRole.user].includes(userDetails?.role) || !isEditable ? "view" : "create",
                data,
                setData,
                dashboard: data,
                setTabId,
                tabZoom,
              }}
            />
            {mode !== "home" && <hr className="mt-4" />}
          </div>
        ) : (
          <div className="mb-3" />
        )}

        {/* body */}

        {iframe ? (
          <>
            {isEditable && (
              <div className="mb-3 d-flex align-items-center gap-4 bg-light p-2">
                {iframe}
                <div style={{ cursor: "pointer" }} onClick={() => setIframe(null)}>
                  <IoMdCloseCircleOutline />
                </div>
              </div>
            )}
            <iframe title="Dashboard" src={iframe} style={{ width: "100%", height: "60vh" }}>
              Iframe does not supported
            </iframe>
          </>
        ) : isLive ? (
          <div className="pb-5 h-100" style={{ minHeight: "60vh" }}>
            <ReactFlowProvider key={`${id}-${currentData?.hasTabs ? (activeTab?.tabId || activeTab?._id || "default") : "single"}`}>
              <LiveDashboardCanvas
                key={`${id}-${currentData?.hasTabs ? (activeTab?.tabId || activeTab?._id || "default") : "single"}`}
                dashboardId={id}
                tabId={currentData?.hasTabs ? (activeTab?.tabId || activeTab?._id || "") : ""}
                tab={
                  currentData?.hasTabs
                    ? activeTab
                    : (activeTab?.liveCanvas?.nodes?.length
                        ? activeTab
                        : (currentData?.liveCanvas?.nodes?.length
                            ? { liveCanvas: currentData.liveCanvas }
                            : activeTab))
                }
                setData={setData}
                readOnly={!isEditable}
                dashboard={currentData}
              />
            </ReactFlowProvider>
          </div>
        ) : (gridData?.length > 0 || isEditable || (hasTabs && tabData?.length > 0)) ? (
          <div className="pb-5">
            <ReactFlowProvider key={`tile-vp-${id}-${tabData?.find((tab) => tab?.isSelected)?.tabId || 'default'}`}>
              <TileDashboardViewport
                gridData={gridData}
                setGridData={setGridData}
                layouts={layouts}
                setLayouts={setLayouts}
                mode={isEditable ? "create" : "view"}
                setEditItem={setEditItem}
                setComponentModal={setComponentModal}
                setData={setData}
                savedViewport={tileViewport}
                onViewportChange={onTileViewportChange}
              />
            </ReactFlowProvider>
          </div>
        ) : (
          <div className="m-auto py-5">
            <Empty />
          </div>
        )}

        {/* buttons */}
        {!["home", "view"].includes(mode) && (
          <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
            <Button type="primary" onClick={handleSubmit} disabled={data?.business?.length <= 0 || data?.site?.length <= 0}>
              {cp === "true" ? "Create" : mode === "edit" ? "Update" : "Create"}
            </Button>
            <Button type="primary" className="bg-secondary" onClick={() => navigate("/dashboard-list")} htmlType="button">
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="relative" style={{ zIndex: 999999 }}>
        {updateDashboardModal && (
          <MetaDashboardModal
            {...{
              show: updateDashboardModal,
              setShow: setUpdateDashboardModal,
              mode: cp === "true" ? "copy" : "edit",
              data,
              setData,
              getDashboard,
            }}
          />
        )}
        {componentModal && (
          <DashboardComponentModal
            {...{
              show: componentModal,
              setShow: setComponentModal,
              editItem,
              setEditItem,
              setAddItemModal,
              setGridData,
              gridData,
              iframe,
              type: data?.type || "tile",
            }}
          />
        )}
        {addItemModal && (
          <DashboardAddItemModal
            {...{
              show: addItemModal,
              setShow: setAddItemModal,
              setComponentModal,
              mode: gridData.find((item) => item._id === editItem._id) ? "edit" : "create",
              editItem,
              setEditItem,
              data,
              gridData,
              setGridData,
              setData,
              iframe,
              setIframe,
            }}
          />
        )}
      </div>
    </Spin>
  );
};

export default DashboardBody;

DashboardBody.propTypes = {
  mode: PropTypes.string,
  id: PropTypes.string,
  setTabId: PropTypes.func.isRequired,
  isFullScreenActive: PropTypes.bool,
  data: PropTypes.object.isRequired,
  setData: PropTypes.func.isRequired,
};
